import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Link } from 'react-router';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, componentStack: '' };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
    this.setState({ componentStack: errorInfo.componentStack ?? '' });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
          <ExclamationTriangleIcon
            className="h-16 w-16 text-primary-600 dark:text-primary-400"
            aria-hidden="true"
          />
          <h1 className="mt-6 text-2xl font-semibold text-surface-900 dark:text-surface-100">
            {this.props.fallbackTitle ?? 'Something went wrong'}
          </h1>
          <p className="mt-2 max-w-md text-surface-500 dark:text-surface-400">
            We encountered an unexpected error. Please try again.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <div className="mt-6 w-full max-w-2xl text-left">
              <p className="text-sm font-semibold text-red-700 dark:text-red-400">{this.state.error.message}</p>
              <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-red-50 p-4 text-xs text-red-800 dark:bg-red-950 dark:text-red-300">{this.state.error.stack}</pre>
              {this.state.componentStack && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-surface-500">Component Stack</summary>
                  <pre className="mt-1 max-h-48 overflow-auto rounded-lg bg-surface-100 p-4 text-xs dark:bg-surface-800">{this.state.componentStack}</pre>
                </details>
              )}
            </div>
          )}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button onClick={() => window.location.reload()} size="lg">
              Try Again
            </Button>
            <Link
              to="/"
              className="inline-flex items-center rounded-lg border border-surface-300 px-6 py-3 text-sm font-semibold text-surface-700 transition-colors hover:bg-surface-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:border-surface-600 dark:text-surface-200 dark:hover:bg-surface-800 dark:focus-visible:ring-offset-surface-900"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
